import { minorCardsList, checkIfNextCourtCard } from '#lib/domain/cards.ts';
import { MAX_SPREAD_CARDS } from '#lib/domain/constants.ts';
import { MinorCardId } from '#lib/domain/types.ts';
import { createNewUniqueArray } from '#lib/utils/array.util.ts';
import { getAvailableCards, pickRandomCard } from '../domain/card-dealer';
import type { MinorCardState } from '../domain/game.state.validation';

export type MinorCardsView = {
	readonly isDisplayingFinaleDeck: boolean;
	readonly spread: readonly MinorCardId[];
	readonly finaleDeck: readonly MinorCardId[];
	readonly discarded: readonly MinorCardId[];
	readonly flipped: readonly MinorCardId[];
	readonly numFinaleDeckCards: number;
	readonly remainingCards: number;
	readonly isCardUsed: (id: MinorCardId) => boolean;
	readonly isCardFlipped: (id: MinorCardId) => boolean;
	readonly isCardDrawn: (id: MinorCardId) => boolean;
};

export class MinorArcanaState {
	public isDisplayingFinaleDeck = $state(true);
	public spread = $state<MinorCardId[]>([]);
	public finaleDeck = $state<MinorCardId[]>([]);
	public discarded = $state<MinorCardId[]>([]);
	public flipped = $state<MinorCardId[]>([]);

	public static get initialState(): MinorCardState {
		return { isDisplayingFinaleDeck: true, discarded: [], spread: [], flipped: [], finaleDeck: [] };
	}

	get numFinaleDeckCards() {
		return this.finaleDeck.length;
	}

	get remainingCards() {
		return this.availableCards.length;
	}

	get visibleMinorCards() {
		return this.spread.filter((cardId) => !this.isCardUsed(cardId));
	}

	get hasMaxFlippedCards() {
		return this.flipped.length === MAX_SPREAD_CARDS;
	}

	get mayClearSpread() {
		return (
			this.spread.length === MAX_SPREAD_CARDS &&
			this.spread.every((cardId) => this.isCardFlipped(cardId))
		);
	}

	get mayDrawCard() {
		return this.visibleMinorCards.length < MAX_SPREAD_CARDS && this.remainingCards > 0;
	}

	reset = () => {
		this.restoreState(MinorArcanaState.initialState);
	};

	restoreState = (state: MinorCardState) => {
		this.spread = [...state.spread];
		this.discarded = [...state.discarded];
		this.flipped = [...state.flipped];
		this.finaleDeck = [...state.finaleDeck];
		this.isDisplayingFinaleDeck = state.isDisplayingFinaleDeck;
	};

	toDTO = (): MinorCardState => {
		return {
			isDisplayingFinaleDeck: this.isDisplayingFinaleDeck,
			discarded: [...this.discarded],
			spread: [...this.spread],
			flipped: [...this.flipped],
			finaleDeck: [...this.finaleDeck]
		};
	};

	maySaveCard = (cardId: MinorCardId): boolean => {
		return checkIfNextCourtCard(cardId, this.finaleDeck);
	};

	clearSpread = () => {
		this.discarded.push(...this.spread);
		this.discarded = this.discarded.filter((card, index, arr) => arr.indexOf(card) === index);
		this.spread = [];
		this.flipped = [];
	};

	toggleFinaleDeck = () => {
		this.isDisplayingFinaleDeck = !this.isDisplayingFinaleDeck;
	};

	isCardUsed = (id: MinorCardId) => {
		return this.discarded.includes(id);
	};

	isCardFlipped = (id: MinorCardId) => {
		return this.flipped.includes(id);
	};

	isCardDrawn = (id: MinorCardId) => {
		return this.spread.includes(id);
	};

	save = (id: MinorCardId) => {
		if (checkIfNextCourtCard(id, this.finaleDeck)) {
			this.spread = this.spread.filter((cardId) => cardId !== id);
			this.finaleDeck = createNewUniqueArray(this.finaleDeck, id);
		}
	};

	flip = (id: MinorCardId) => {
		if (this.isCardFlipped(id)) return;

		this.flipped = createNewUniqueArray(this.flipped, id);
	};

	draw = () => {
		if (!this.mayDrawCard) return;

		const newId = pickRandomCard(this.availableCards);
		if (!newId) return;
		this.spread = createNewUniqueArray(this.spread, newId);
	};

	private get availableCards() {
		return getAvailableCards({
			cards: minorCardsList,
			excluded: [...this.spread, ...this.discarded, ...this.finaleDeck]
		});
	}
}
